import type { StandardServicePageContent } from "~/content/servicePage";

export const GasBoilerContent: StandardServicePageContent = {
  heroTitle: "Przegląd kotła gazowego",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Jak często",
      value: "Najlepiej raz w roku, przed sezonem grzewczym",
      description:
        "To najbezpieczniejszy moment na czyszczenie, kontrolę spalania i wychwycenie usterek zanim kocioł zacznie pracować intensywnie przez wiele miesięcy.",
    },
    {
      label: "Co obejmuje",
      value: "Czyszczenie, analizę spalania, kontrolę zabezpieczeń i regulację",
      description:
        "Dobry serwis obejmuje nie tylko oględziny obudowy, ale też ocenę pracy palnika, wymiennika, automatyki, układu spalin i najważniejszych parametrów urządzenia.",
    },
    {
      label: "Kiedy szybciej",
      value: "Przy błędach, spadku mocy, zapachu spalin lub wyższych rachunkach",
      description:
        "To sygnały, że problem może dotyczyć spalania, zabrudzenia wymiennika albo zużycia podzespołów. W takich przypadkach nie warto czekać do kolejnego terminu rocznego.",
    },
    {
      label: "Ile kosztuje",
      value: "Cena zależy od typu kotła, marki i zakresu serwisu",
      description:
        "Standardowy roczny serwis jest wyceniany inaczej niż wizyta z dodatkową diagnostyką, regulacją i oceną starszego urządzenia. Znaczenie ma też dokumentacja po przeglądzie.",
    },
  ],
  faqTitle: "Najczęstsze pytania o przegląd kotła gazowego",
  sections: [
    {
      title: "Na czym polega przegląd kotła gazowego",
      paragraphs: [
        `Przegląd kotła gazowego polega na sprawdzeniu, czy urządzenie pracuje bezpiecznie, stabilnie i z właściwą sprawnością. To nie jest tylko szybki ogląd obudowy, ale serwis obejmujący kontrolę kluczowych podzespołów, ocenę procesu spalania i wychwycenie usterek, które mogą z czasem podnieść zużycie gazu albo doprowadzić do awarii urządzenia.`,
        `W praktyce kocioł gazowy pracuje przez wiele miesięcy bez przerwy, a jego stan pogarsza się stopniowo. Zanieczyszczony palnik, zabrudzony wymiennik ciepła, nieprawidłowe parametry spalania albo zużyte uszczelnienia nie zawsze dają od razu wyraźny sygnał alarmowy, ale wpływają na koszty eksploatacji i bezpieczeństwo domowników.`,
        `Dobrze wykonany przegląd ma dać właścicielowi odpowiedź, czy urządzenie nadaje się do dalszej bezpiecznej pracy, czy wymaga regulacji, czyszczenia albo szerszej naprawy. To szczególnie ważne przed okresem intensywnego użytkowania kotła.`,
      ],
    },
    {
      title: "Jak często serwisować kocioł gazowy i dlaczego najlepiej przed sezonem",
      paragraphs: [
        `W budynku, w którym pracuje kocioł gazowy, obowiązkowa jest co najmniej coroczna kontrola instalacji gazowej oraz przewodów kominowych. Wynika to z art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego i obejmuje ocenę warunków bezpiecznej pracy urządzeń gazowych w obiekcie.`,
        `W praktyce oznacza to, że właściciel nie powinien traktować kotła jako urządzenia pozostawionego bez regularnej kontroli. Osobny, techniczny serwis samego kotła jest też zwykle zalecany corocznie przez producenta, a w wielu przypadkach ma znaczenie dla utrzymania gwarancji i bezawaryjnej eksploatacji urządzenia.`,
        `Najbezpieczniej jest więc przyjąć zasadę corocznego przeglądu i serwisu kotła, najlepiej przed sezonem grzewczym. Takie podejście łączy zgodność z obowiązkami dotyczącymi instalacji gazowej z praktyczną dbałością o stan samego urządzenia.`,
      ],
    },
    {
      title: "Co obejmuje serwis kotła gazowego w domu i mieszkaniu",
      paragraphs: [
        `Serwis kotła gazowego obejmuje zwykle czyszczenie kluczowych elementów urządzenia, ocenę szczelności w obrębie kotła, kontrolę ciśnienia, sprawdzenie pracy pompy, zaworów i automatyki oraz weryfikację układu odprowadzania spalin. W kotłach kondensacyjnych dochodzi też kontrola syfonu kondensatu i elementów odpowiedzialnych za odprowadzenie skroplin.`,
        `Jednym z najważniejszych etapów jest analiza parametrów spalania i ewentualna regulacja pracy palnika. To właśnie od jakości spalania zależy nie tylko sprawność urządzenia, ale również bezpieczeństwo użytkowania i ryzyko problemów takich jak niestabilna praca, większe zużycie paliwa czy pogorszenie komfortu cieplnego.`,
        `W domu jednorodzinnym przegląd często obejmuje kocioł centralnego ogrzewania pracujący dla całego budynku oraz współpracę urządzenia z instalacją c.o. i c.w.u. W mieszkaniu zwykle chodzi o kocioł dwufunkcyjny albo urządzenie odpowiadające za ogrzewanie lokalu i ciepłą wodę użytkową, ale znaczenie regularnego serwisu pozostaje takie samo.`,
      ],
    },
    {
      title: "Najczęstsze problemy z kotłem gazowym i cena przeglądu",
      paragraphs: [
        `Najczęstsze problemy z kotłem gazowym to głośniejsza niż zwykle praca, częste przechodzenie w tryb awarii, spadek wydajności ogrzewania albo ciepłej wody, zwiększone zużycie gazu oraz błędy pojawiające się na sterowniku. Część z tych objawów rozwija się stopniowo i przez długi czas wygląda jak drobna niedogodność, choć w praktyce sygnalizuje pogarszający się stan urządzenia.`,
        `Niepokoić powinny także nietypowy kolor płomienia, zapach spalin, zawilgocenia przy kotle, problemy z ciśnieniem w instalacji czy częste spadki komfortu ciepłej wody. To sytuacje, w których nie chodzi już tylko o sprawność, ale również o bezpieczeństwo użytkowania urządzenia w domu lub mieszkaniu.`,
        `Im wcześniej takie objawy zostaną sprawdzone, tym większa szansa, że skończy się na standardowym serwisie, czyszczeniu albo regulacji. Cena przeglądu kotła gazowego zależy od typu urządzenia, marki, zakresu serwisu i tego, czy chodzi o prostą kontrolę roczną, czy wizytę z dodatkowymi czynnościami diagnostycznymi.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług przeglądu kotła gazowego",
  scopeItems: [
    "Czyszczenie palnika",
    "Kontrola wymiennika ciepła",
    "Analiza spalania",
    "Sprawdzenie układu spalin",
    "Kontrola automatyki i zabezpieczeń",
    "Ocena szczelności przy kotle",
    "Regulacja parametrów pracy",
    "Dokumentacja serwisowa",
  ],
  seo: {
    title: "Przegląd kotła gazowego: serwis, analiza spalin, cena i protokół",
    description:
      "Zamów przegląd kotła gazowego dla domu lub mieszkania. Sprawdź, co obejmuje serwis, kiedy zrobić go przed sezonem grzewczym i co wpływa na cenę usługi.",
  },
};
